import { ThemeEvents, CartUpdateEvent, CartErrorEvent } from '@theme/events';

/**
 * Offer engine upsell source for the cart drawer.
 *
 * Asks the go-offer engine which product to offer for the current cart and paints
 * the answer into the SAME card markup `snippets/cart-upsell.liquid` renders for the
 * product/collection sources. Nothing here introduces a class of its own for the card
 * — the chrome, the grid and the button are the theme's, so switching a store to this
 * source changes what is offered, never how the drawer looks.
 *
 * Degrades to nothing: the whole section (wrapper and title included) ships `hidden`
 * from the server and is only revealed once the engine answers with a usable offer. An
 * unreachable, slow or empty engine leaves the drawer exactly as it would look without
 * the feature.
 *
 * @module offer-engine
 */

/**
 * One offer as returned by `POST /recommend`. The engine answers prices in MAJOR units
 * (reais), while everything Shopify hands us is in cents — every comparison below
 * converts explicitly, because mixing the two silently yields 100%-off badges.
 *
 * @typedef {object} Offer
 * @property {string} sku
 * @property {string|number} variant_id - Arrives as a string; parsed where an id is needed.
 * @property {string} [title]
 * @property {string} [url]
 * @property {string} [image_url]
 * @property {number} [price] - List price, major units.
 * @property {number} [final_price] - Price the engine wants shown, major units.
 * @property {boolean} [closes_benefit]
 * @property {string} [benefit_label]
 */

/**
 * @typedef {object} Decision
 * @property {string} [offer_id]
 * @property {Offer[]} [offers]
 * @property {string|number} [variant_id] - Present when the engine flattened a single offer.
 */

/**
 * @typedef {object} CartLine
 * @property {string} [sku]
 * @property {number} variant_id
 * @property {number} quantity
 * @property {number} price
 * @property {number} [final_price]
 * @property {number} [final_line_price]
 * @property {Record<string, string>} [properties]
 */

/**
 * @typedef {object} Cart
 * @property {CartLine[]} items
 * @property {number} total_price
 * @property {number} [item_count]
 */

/**
 * A merchant-configured reward threshold, in the same unit as the cart subtotal (cents).
 *
 * @typedef {object} Goal
 * @property {number} value
 * @property {string} label
 */

/**
 * @typedef {Goal & {tier: number}} RankedBenefit
 */

/**
 * @typedef {object} RecommendPayload
 * @property {string|undefined} brand
 * @property {string} surface
 * @property {string} goal
 * @property {number} n
 * @property {Array<{sku: string, variant_id: number, qty: number, price: number}>} cart
 * @property {number} cart_total
 * @property {{is_returning: boolean}} customer
 * @property {number} [gap]
 * @property {{value: number, label: string}} [threshold]
 */

/**
 * The engine decides in ~300 ms; the round trip to the edge is what rules the clock.
 * Measured from Brazil on mobile, the first call of a session pays a CORS preflight
 * (~520 ms) plus the POST (~1400 ms) while competing with the rest of the page — a
 * 2500 ms budget aborted it in production and the carousel vanished intermittently with
 * no error. Stretching costs nothing visually: the section is born hidden, so the worst
 * case is a card arriving late, not a hole in the drawer.
 */
const RECOMMEND_TIMEOUT_MS = 6000;
const PRODUCT_TIMEOUT_MS = 4000;

/** Collapses a burst of cart updates (holding a quantity stepper) into one call. */
const REFRESH_DEBOUNCE_MS = 150;

const IMPRESSION_DEDUP_PREFIX = 'offer:imp:';

const SOURCE_ID = 'offer-engine';

/**
 * Every checkout control this theme knows about. Kept in sync with the same list in
 * `assets/cart-gift-guard.js`, `assets/cart-gifts-progress.js` and
 * `assets/analytics-diagnostics.js` — the Yampi app injects its own buttons, and a
 * narrower selector loses the event on the only path that actually reaches checkout.
 */
const CHECKOUT_SELECTOR =
  '#checkout, [name="checkout"], .cart__checkout-button, .ymp_check_mainBtn, .yampi-btn-finalize-single, a.btn-checkout[href="/checkout"]';

/** A line priced at or below this (in cents) is a gift, not a purchase. */
const GIFT_PRICE_CEILING = 2;

/**
 * The engine names its events short; the analytics layer needs the `offer_` prefix so
 * they never collide with the `upsell_*` events another layer already emits.
 *
 * @type {Record<string, string>}
 */
const ENGINE_EVENT_NAMES = {
  offer_impression: 'impression',
  offer_accept: 'accept',
  offer_reject: 'reject',
  offer_checkout: 'checkout',
};

class OfferEngineComponent extends HTMLElement {
  /** @type {Cart|null} */
  #cart = null;
  /**
   * The engine mints one decision id per `/recommend` call and every card of that call
   * shares it — it is the key `POST /event` expects, not a per-product id.
   * @type {string|null}
   */
  #offerId = null;
  /**
   * Monotonic refresh ticket. Compared after every await so a superseded refresh neither
   * paints nor hides; see #refresh.
   * @type {number}
   */
  #generation = 0;
  /** @type {Offer[]} */
  #offers = [];
  /**
   * Accepted and rejected offers, both keyed by `${offerId}:${sku}`. The decision prefix
   * is what makes them symmetric: reopening the drawer must not re-reject an offer the
   * shopper already skipped, but a NEW decision from the engine has to be able to record
   * both outcomes again for the same product.
   * @type {Set<string>}
   */
  #accepted = new Set();
  /** @type {Set<string>} */
  #rejected = new Set();
  /** @type {IntersectionObserver|null} */
  #observer = null;
  /** @type {AbortController|null} */
  #controller = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #refreshTimer = null;
  /**
   * Product JSON by handle. A render happens on every cart update while the drawer is
   * open, and the authoritative price never changes between them, so without this each
   * update would re-fetch one document per card.
   * @type {Map<string, Promise<any>>}
   */
  #productCache = new Map();

  connectedCallback() {
    if (!this.dataset.apiUrl) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect, { threshold: 0.5 });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    // Applying a discount does not emit `cart:update` — cart-discount.js emits
    // `discount:update` and the cart section re-renders. Without this the offers
    // disappear for the rest of the session after a coupon.
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    // Capture phase: dialog close events are dispatched without `bubbles`, so a
    // bubble-phase listener on document never sees them.
    document.addEventListener('dialog:close', this.#handleDialogClose, true);
    document.addEventListener('click', this.#handleCheckoutClick, true);
    this.addEventListener('click', this.#handleAddToCart);

    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
    document.removeEventListener('dialog:close', this.#handleDialogClose, true);
    document.removeEventListener('click', this.#handleCheckoutClick, true);
    this.removeEventListener('click', this.#handleAddToCart);

    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  /** @returns {string} */
  get #apiUrl() {
    return (this.dataset.apiUrl ?? '').replace(/\/$/, '');
  }

  /** @returns {number} */
  get #maxCards() {
    return Math.max(1, Math.min(12, parseInt(this.dataset.maxCards ?? '', 10) || 4));
  }

  /**
   * The whole upsell section, which is what has to disappear when there is nothing to
   * offer — hiding only this element would leave the merchant's title over an empty box.
   *
   * @returns {HTMLElement|null}
   */
  get #section() {
    const section = this.closest('[data-cart-upsell]');
    return section instanceof HTMLElement ? section : null;
  }

  /**
   * The merchant's reward goals, in cents, nearest first. Emitted by
   * `snippets/offer-engine.liquid`, which reads the SAME settings the progress bar
   * reads and emits an empty list when the benefit highlight is switched off.
   *
   * @returns {Goal[]}
   */
  get #thresholds() {
    try {
      const parsed = JSON.parse(this.dataset.thresholds || '[]');
      if (!Array.isArray(parsed)) return [];
      return /** @type {Goal[]} */ (parsed.filter((goal) => Number.isFinite(goal?.value))).sort(
        (a, b) => a.value - b.value
      );
    } catch {
      return [];
    }
  }

  /** @returns {Set<number>} Variant ids the merchant configured as gifts. */
  get #giftVariantIds() {
    const raw = this.dataset.giftVariantIds ?? '';
    return new Set(
      raw
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => Number.isFinite(id))
    );
  }

  /**
   * Cart subtotal in cents with gift lines excluded, applying all THREE rules the
   * progress bar applies (`snippets/cart-gifts-progress.liquid`): the explicit gift
   * property, a configured gift variant id, and a token price. Dropping any one of them
   * makes this subtotal drift from the bar's, and then a card promises a reward the bar
   * says is still far away.
   *
   * @returns {number}
   */
  #benefitSubtotal() {
    const items = this.#cart?.items;
    if (!Array.isArray(items)) return 0;

    // `cart-benefit-rules.liquid` publishes whether the bar this ribbon mirrors excludes
    // gift lines: the gift progress bar does, the free shipping bar reads cart.total_price
    // raw. Excluding unconditionally would make the component disagree with the very bar
    // drawn above it — it would call a goal unreached that the bar already shows as won.
    if (this.dataset.excludeGifts !== 'true') {
      return Number(this.#cart?.total_price ?? 0);
    }

    const giftVariantIds = this.#giftVariantIds;

    return items.reduce((total, item) => {
      if (item?.properties?._gobeautexCartGift) return total;
      if (giftVariantIds.has(Number(item?.variant_id))) return total;
      if (Number(item?.final_price) <= GIFT_PRICE_CEILING) return total;
      return total + Number(item?.final_line_price ?? 0);
    }, 0);
  }

  /**
   * The nearest goal the shopper has not reached yet, or null when everything is
   * unlocked. This is what the engine is told about, because the smallest gap is the one
   * worth optimizing a recommendation against.
   *
   * @returns {Goal|null}
   */
  #nextThreshold() {
    const subtotal = this.#benefitSubtotal();
    return this.#thresholds.find((goal) => goal.value > subtotal) ?? null;
  }

  /**
   * The BIGGEST unreached goal that adding one of this offer reaches, or null.
   *
   * Highest rather than nearest on purpose: a product that clears both the R$99 and the
   * R$149 goal should advertise the R$149 reward, because the bigger carrot is the
   * stronger incentive and the smaller one is unlocked along the way anyway.
   *
   * Computed here rather than trusted from the API. `closes_benefit` is the engine
   * asserting it closed a gap it was TOLD about; if the engine does not know this
   * store's thresholds the flag never arrives and the ribbon stays silently dark. The
   * theme owns the gamification, so the theme decides, and the flag is never honoured on
   * its own.
   *
   * @param {Offer} offer
   * @param {number} priceCents - The authoritative price, in cents.
   * @returns {RankedBenefit|null}
   */
  #benefitFor(offer, priceCents) {
    // The opt-in gates EVERY path, including the engine-flag fallback below — otherwise
    // a store that never switched the highlight on would still get a ribbon (and the
    // tier reordering) as soon as the engine volunteered the flag.
    if (this.dataset.benefitHighlight !== 'true') return null;

    // No fallback to the engine's own `closes_benefit`: with no thresholds the theme
    // cannot verify the claim, and an unverifiable promise on the cart is worse than no
    // ribbon. An empty list is also exactly what the Liquid emits for a quantity-mode
    // goal, which is not comparable to a price at all.
    const goals = this.#thresholds;
    if (!goals.length) return null;

    const subtotal = this.#benefitSubtotal();
    const unreached = goals.filter((goal) => goal.value > subtotal);
    if (!unreached.length || !Number.isFinite(priceCents)) return null;

    const after = subtotal + priceCents;
    for (let index = unreached.length - 1; index >= 0; index -= 1) {
      const goal = unreached[index];
      if (goal && after >= goal.value) return { ...goal, tier: index + 1 };
    }
    return null;
  }

  /**
   * The cart UI must never wait on the engine, so this only ever schedules work.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event)?.detail;
    const resource = detail?.resource;
    const isFullCart = Array.isArray(resource?.items) && typeof resource?.total_price === 'number';
    this.#cart = isFullCart ? /** @type {Cart} */ (resource) : null;
    this.#scheduleRefresh();
  };

  #scheduleRefresh() {
    if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
    this.#refreshTimer = setTimeout(() => {
      this.#refreshTimer = null;
      this.#refresh();
    }, REFRESH_DEBOUNCE_MS);
  }

  /** A drawer closed after an offer was seen but never added is a rejection. */
  #handleDialogClose = () => {
    for (const offer of this.#offers) {
      const key = `${this.#offerId}:${offer.sku}`;
      if (this.#accepted.has(key) || this.#rejected.has(key)) continue;
      // Only reject what was SEEN. The track shows one card at a time, so without this
      // an open-and-close without scrolling produced 1 impression and N rejections.
      try {
        if (!sessionStorage.getItem(`${IMPRESSION_DEDUP_PREFIX}${key}`)) continue;
      } catch {
        continue;
      }
      this.#rejected.add(key);
      this.#emit('offer_reject', offer);
    }
  };

  /**
   * Checkout intent while an engine-sourced line is still in the cart. Capture phase,
   * because the Yampi redirect leaves the page on this click. Nothing here cancels or
   * delays the click: no preventDefault, no stopPropagation, and the only work is a
   * keepalive fetch.
   *
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest(CHECKOUT_SELECTOR)) return;

    const line = this.#cart?.items?.find((item) => item.properties?._offer_id);
    if (!line?.properties) return;

    this.#send('offer_checkout', line.properties._offer_id ?? null, {
      offer_sku: line.properties._offer_sku ?? '',
      anchor_sku: '',
      offer_price: String(line.price / 100),
    });
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const card = /** @type {HTMLElement} */ (entry.target);
      const offer = this.#offers.find((item) => item.sku === card.dataset.offerSku);

      this.#observer?.unobserve(card);
      if (!offer) continue;

      const dedupKey = `${IMPRESSION_DEDUP_PREFIX}${this.#offerId}:${offer.sku}`;
      try {
        if (sessionStorage.getItem(dedupKey)) continue;
        sessionStorage.setItem(dedupKey, '1');
      } catch {
        // Private mode: emit anyway, the engine dedups server-side.
      }

      this.#emit('offer_impression', offer);
    }
  };

  /** @param {MouseEvent} event */
  #handleAddToCart = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-offer-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (button.getAttribute('aria-busy') === 'true') return;

    event.preventDefault();

    const offer = this.#offers.find((item) => item.sku === button.dataset.offerSku);
    if (!offer) return;

    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(String(offer.variant_id), 10),
          quantity: 1,
          // Attribution rides on the line, which is how `offer_checkout` can tell an
          // engine-sourced purchase from any other line at checkout time.
          properties: { _offer_id: this.#offerId, _offer_sku: offer.sku, _recommendation: '1' },
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload.status || payload.errors) {
        throw new Error(payload.description || payload.message || this.dataset.errorText);
      }

      this.#accepted.add(`${this.#offerId}:${offer.sku}`);
      // Fire-and-forget, and before the cart below is refetched — `#emit` reads the
      // anchor sku off the cart as it was when the offer was shown.
      this.#emit('offer_accept', offer);

      const cart = /** @type {Cart} */ (await (await fetch(Theme.routes.cart_url + '.js')).json());
      document.dispatchEvent(
        new CartUpdateEvent(cart, SOURCE_ID, {
          source: SOURCE_ID,
          itemCount: cart?.item_count ?? 0,
          productId: payload.product_id ?? null,
        })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      document.dispatchEvent(
        new CartErrorEvent(
          SOURCE_ID,
          message || this.dataset.errorText || '',
          this.dataset.errorHeading || '',
          {}
        )
      );
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  /** Asks the engine for offers for the current cart and renders the result. */
  async #refresh() {
    // The ticket is taken BEFORE the first await, not just before /recommend: the two
    // early returns below also mutate shared state, and an undebounced first refresh
    // whose /cart.js fails would otherwise wipe the carousel a newer one just painted.
    const generation = ++this.#generation;

    if (!this.#cart) {
      try {
        this.#cart = /** @type {Cart} */ (await (await fetch(Theme.routes.cart_url + '.js')).json());
      } catch {
        if (generation === this.#generation) this.#hide();
        return;
      }
    }

    if (!this.#cart?.items?.length) {
      if (generation === this.#generation) this.#hide();
      return;
    }

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    // The timer closes over THIS controller, never over the field: reading the field at
    // fire time would let a stale timer abort whatever request happens to be in flight
    // by then, and the carousel would vanish while the engine was answering normally.
    const timeout = setTimeout(() => controller.abort(), RECOMMEND_TIMEOUT_MS);

    try {
      const response = await fetch(`${this.#apiUrl}/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify(this.#recommendPayload()),
      });

      if (!response.ok) throw new Error(`Engine responded ${response.status}`);

      const decision = /** @type {Decision} */ (await response.json());
      if (generation !== this.#generation) return;

      const offers = this.#readOffers(decision).slice(0, this.#maxCards);
      await this.#render(offers, generation, decision.offer_id ?? null);
    } catch {
      // A superseded refresh must not hide the carousel the winning one just painted.
      if (generation === this.#generation) this.#hide();
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * The engine flattens the top offer onto the response and only adds `offers[]` when
   * more than one was asked for; an empty `offers[]` is its "no offer" answer.
   *
   * @param {Decision} decision
   * @returns {Offer[]}
   */
  #readOffers(decision) {
    if (Array.isArray(decision.offers)) return decision.offers;
    return decision.variant_id ? [/** @type {Offer} */ (/** @type {unknown} */ (decision))] : [];
  }

  /** @returns {RecommendPayload} */
  #recommendPayload() {
    const items = this.#cart?.items ?? [];

    /** @type {RecommendPayload} */
    const payload = {
      brand: this.dataset.brand,
      surface: this.dataset.surface || 'cart',
      goal: this.dataset.goal || 'aov',
      n: this.#maxCards,
      cart: items.map((item) => ({
        sku: item.sku || '',
        variant_id: item.variant_id,
        qty: item.quantity,
        price: item.price / 100,
      })),
      cart_total: (this.#cart?.total_price ?? 0) / 100,
      customer: { is_returning: this.dataset.segment === 'returning' },
    };

    // `gap` carries the number and `threshold.label` the copy, so both are sent.
    const next = this.#nextThreshold();
    if (next) {
      payload.gap = (next.value - this.#benefitSubtotal()) / 100;
      payload.threshold = { value: next.value / 100, label: next.label };
    }

    return payload;
  }

  /**
   * Resolves each offer's price against Shopify BEFORE painting, then renders.
   *
   * The price on the card has to be the price the cart will charge: the add-to-cart
   * posts only a `variant_id`, so Shopify — not the engine — decides what is billed. A
   * stale engine catalogue would otherwise advertise a discount the cart does not
   * honour, and the ribbon would promise a reward computed from a price that is not
   * real. The Liquid-rendered sources have the same guarantee for free, because they
   * render `product.price`.
   *
   * The lookup is same-origin and CDN-cached, and its result is memoised per handle, so
   * a cart update re-renders without re-fetching.
   *
   * @param {Offer[]} offers
   * @param {number} generation - The refresh ticket this render belongs to.
   * @param {string|null} offerId - The decision id, committed only if this render wins.
   * @returns {Promise<void>}
   */
  async #render(offers, generation, offerId) {
    const container = this.querySelector('[data-offer-cards]');
    if (!container) return this.#hide();

    const valid = offers.filter((offer) => offer?.sku && offer?.variant_id);
    if (!offerId || !valid.length) return this.#hide();

    const priced = await Promise.all(valid.map((offer) => this.#resolvePrice(offer)));

    // Product lookups are not abortable, so the ticket is re-checked here: without this,
    // a slow decision would paint its cards after a newer one already won.
    if (generation !== this.#generation) return;

    const decorated = priced
      .filter((entry) => Number.isFinite(entry.price))
      .map((entry) => ({
        ...entry,
        // Unverified price => no ribbon. The card still renders (the engine's number is
        // better than no card), but it may not make a promise the bar would have to keep.
        benefit: entry.verified ? this.#benefitFor(entry.offer, entry.price) : null,
      }));

    if (!decorated.length) return this.#hide();

    // The engine already ranks by expected value (margin × affinity × take rate). The
    // storefront overrides exactly ONE criterion on top of that: the reward tier an
    // offer unlocks, biggest first — it is the strongest conversion lever and the card
    // that justifies the offer existing. `sort` is stable since ES2019, so the engine's
    // order survives inside each tier.
    decorated.sort((a, b) => (b.benefit?.tier ?? 0) - (a.benefit?.tier ?? 0));

    this.#offers = decorated.map((entry) => entry.offer);
    this.#offerId = offerId;

    const cards = decorated.map((entry) => this.#card(entry, entry.benefit?.label));
    // Detached cards stay reachable through the observer's strong references to its
    // targets, so the previous set is dropped before the new one is observed.
    this.#observer?.disconnect();
    container.replaceChildren(...cards);

    this.hidden = false;
    this.#section?.removeAttribute('hidden');

    for (const card of cards) this.#observer?.observe(card);
  }

  /**
   * The authoritative price and compare-at for an offer, in cents, read from the
   * product JSON. Falls back to the engine's own number only when the product cannot be
   * resolved — better a card with the engine's price than no card at all, and the
   * add-to-cart is unaffected either way.
   *
   * `verified` says whether the number came from Shopify or fell back to the engine's.
   * The ribbon must never be computed from an unverified price: the card would promise a
   * reward the bar cannot grant, and the shopper reads both in the same glance.
   *
   * @param {Offer} offer
   * @returns {Promise<{offer: Offer, price: number, compareAt: number|null, verified: boolean}>}
   */
  async #resolvePrice(offer) {
    const engineCents = Math.round(Number(offer.final_price ?? offer.price) * 100);
    const handle = this.#productHandle(offer.url);
    if (!handle) return { offer, price: engineCents, compareAt: null, verified: false };

    try {
      let request = this.#productCache.get(handle);
      if (!request) {
        // Timed out on its own: this lookup is not covered by the /recommend controller,
        // and a socket that never settles would wedge Promise.all — and with it every
        // later refresh — leaving the carousel frozen on stale cards.
        request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
          signal: AbortSignal.timeout(PRODUCT_TIMEOUT_MS),
        }).then((response) => (response.ok ? response.json() : null));
        this.#productCache.set(handle, request);
      }

      const product = await request;
      // No fallback to the first variant: pricing a card from ANOTHER variant is worse
      // than falling back to the engine's number.
      const variant = product?.variants?.find(
        (/** @type {{id: string|number}} */ item) => String(item.id) === String(offer.variant_id)
      );
      if (!variant || !Number.isFinite(Number(variant.price))) {
        return { offer, price: engineCents, compareAt: null, verified: false };
      }

      const compareAt = Number(variant.compare_at_price);
      return {
        offer,
        price: Number(variant.price),
        compareAt: Number.isFinite(compareAt) && compareAt > Number(variant.price) ? compareAt : null,
        verified: true,
      };
    } catch {
      return { offer, price: engineCents, compareAt: null, verified: false };
    }
  }

  /**
   * @param {string} [url] - Product URL returned by the engine.
   * @returns {string|null}
   */
  #productHandle(url) {
    if (typeof url !== 'string') return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match?.[1] ?? null;
  }

  /**
   * Fills the shop's own money format, the way the `money` Liquid filter does.
   *
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      const comma = String(token).includes('with_comma_separator');
      const fixed = (cents / 100).toFixed(String(token).startsWith('amount_no_decimals') ? 0 : 2);
      const [whole, fraction] = fixed.split('.');
      const grouped = (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
      return fraction ? `${grouped}${comma ? ',' : '.'}${fraction}` : grouped;
    });
  }

  /**
   * Builds a card in the theme's own upsell markup, with `textContent` only — the
   * engine's payload is never injected as HTML.
   *
   * @param {{offer: Offer, price: number, compareAt: number|null}} entry
   * @param {string} [benefitLabel] - Label of the reward this offer unlocks, if any.
   * @returns {HTMLElement}
   */
  #card(entry, benefitLabel) {
    const { offer, price, compareAt } = entry;

    const card = document.createElement('article');
    card.className = this.dataset.slideClasses || 'cart-upsell__slide';
    card.setAttribute('role', 'listitem');
    card.dataset.offerSku = offer.sku;

    if (benefitLabel) {
      card.classList.add('cart-upsell__slide--benefit');
      card.append(this.#benefitTag(benefitLabel));
    }

    const productUrl = this.#productUrl(offer.url);
    const linkable = Boolean(productUrl);
    const media = document.createElement(linkable ? 'a' : 'div');
    media.className = 'cart-upsell__media';
    if (media instanceof HTMLAnchorElement && productUrl) {
      media.href = productUrl;
      media.setAttribute('aria-label', offer.title ?? '');
    }
    const imageUrl = this.#imageUrl(offer.image_url);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'cart-upsell__image';
      image.src = imageUrl;
      image.alt = offer.title ?? '';
      image.loading = 'lazy';
      image.width = 80;
      image.height = 80;
      media.append(image);
    }
    card.append(media);

    const content = document.createElement('div');
    content.className = 'cart-upsell__content';

    const title = document.createElement(linkable ? 'a' : 'span');
    title.className = 'cart-upsell__product-title';
    title.textContent = offer.title ?? '';
    if (title instanceof HTMLAnchorElement && productUrl) title.href = productUrl;
    content.append(title);

    const prices = document.createElement('div');
    prices.className = 'cart-upsell__prices';

    if (compareAt != null) {
      const compare = document.createElement('s');
      compare.className = 'cart-upsell__compare-price';
      compare.textContent = this.#money(compareAt);
      prices.append(compare);
    }

    const priceNode = document.createElement('span');
    priceNode.className = 'cart-upsell__price';
    priceNode.textContent = this.#money(price);
    prices.append(priceNode);

    if (compareAt != null && this.dataset.showBadge === 'true') {
      const badge = document.createElement('span');
      badge.className = 'cart-upsell__badge';
      badge.textContent = this.#badgeText(Math.round((1 - price / compareAt) * 100));
      prices.append(badge);
    }

    content.append(prices);

    const button = this.#atcButton(offer);
    const labelled = this.dataset.atcStyle === 'label';
    if (labelled) content.append(button);

    card.append(content);
    if (!labelled) card.append(button);

    return card;
  }

  /**
   * @param {number} percent
   * @returns {string}
   */
  #badgeText(percent) {
    // The template arrives translated with the locale's own `{{ percent }}` placeholder
    // left unfilled, so the word order around the number stays the translator's instead
    // of being concatenated here. Interpolation is the only thing done in JS.
    const template = this.dataset.badgeTemplate || '{{ percent }}%';
    return template.replace('{{ percent }}', String(percent));
  }

  /**
   * The ribbon for the benefit this offer unlocks — in practice, free shipping. Same
   * classes the Liquid-rendered cards use, and the icon is cloned from the template the
   * snippet rendered, so there is one style and one drawing.
   *
   * @param {string} label
   * @returns {HTMLSpanElement}
   */
  #benefitTag(label) {
    const tag = document.createElement('span');
    tag.className = 'cart-upsell__benefit-tag';

    const template = this.querySelector('template[data-offer-benefit-icon]');
    if (template instanceof HTMLTemplateElement) {
      tag.append(template.content.cloneNode(true));
    }

    const text = document.createElement('span');
    text.textContent = label;
    tag.append(text);

    return tag;
  }

  /**
   * @param {Offer} offer
   * @returns {HTMLButtonElement}
   */
  #atcButton(offer) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cart-upsell__add-button';
    button.dataset.offerAdd = '';
    button.dataset.offerSku = offer.sku;

    const label = this.dataset.atcText ?? '';
    button.setAttribute('aria-label', `${label}: ${offer.title ?? ''}`.trim());

    if (this.dataset.atcStyle === 'label') {
      const text = document.createElement('span');
      text.className = 'cart-upsell__add-label';
      text.textContent = label;
      button.append(text);
    } else {
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'cart-upsell__add-icon');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('fill', 'none');
      icon.setAttribute('stroke', 'currentColor');
      icon.setAttribute('stroke-width', '2');
      icon.setAttribute('stroke-linecap', 'round');
      icon.setAttribute('stroke-linejoin', 'round');
      icon.setAttribute('aria-hidden', 'true');
      for (const d of ['M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z', 'M12 11v6', 'M9 14h6']) {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', d);
        icon.append(path);
      }
      button.append(icon);
    }

    const spinner = document.createElement('span');
    spinner.className = 'cart-upsell__add-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    button.append(spinner);

    return button;
  }

  /**
   * This storefront's URL for the product the engine offered, or null when the engine's
   * URL carries no recognisable product handle.
   *
   * @param {string} [value] - The engine's URL, read for its handle only.
   * @returns {string|null}
   */
  #productUrl(value) {
    // Built from the handle against THIS storefront, never taken from the engine. A
    // scheme check is not enough: `https://evil.example/pay-now` passes it, and the card
    // is a trusted-looking link on the highest-intent surface of the store. Rebuilding
    // locally also survives the engine being configured with another domain, which is
    // exactly what it returns today (the production host, not the preview one).
    const handle = this.#productHandle(value);
    if (!handle) return null;
    // Unprefixed on purpose: the theme exposes no storefront root in `Theme.routes`, and
    // every other product path in this codebase is built the same way. On a market with
    // a URL prefix the link would leave the active market — no store in the fleet runs
    // one today, and fixing it means adding the root to the shared routes object.
    return `/products/${encodeURIComponent(handle)}`;
  }

  /**
   * An image URL the storefront is willing to load, or null.
   *
   * Restricted to same-origin and the Shopify CDN: any other host would turn every
   * cart-drawer open into a request to a third party, leaking the shopper's IP, user
   * agent and referer, and would let arbitrary artwork be presented as a product photo.
   *
   * @param {string} [value]
   * @returns {string|null}
   */
  #imageUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' && url.origin !== window.location.origin) return null;
      const allowed = url.origin === window.location.origin || url.hostname === 'cdn.shopify.com';
      return allowed ? url.href : null;
    } catch {
      return null;
    }
  }

  #hide() {
    this.#offers = [];
    this.#offerId = null;
    this.hidden = true;
    this.#section?.setAttribute('hidden', '');
    this.querySelector('[data-offer-cards]')?.replaceChildren();
  }

  /**
   * @param {string} name
   * @param {Offer} offer
   */
  #emit(name, offer) {
    this.#send(name, this.#offerId, {
      offer_sku: offer.sku || '',
      anchor_sku: this.#cart?.items?.[0]?.sku || '',
      offer_price: String(offer.final_price ?? offer.price ?? ''),
    });
  }

  /**
   * Sends the event to the engine (which feeds the bandit) and, separately, to the
   * analytics layer. Auditing must never be able to break the engine feedback, so the
   * two are independent calls and neither awaits the other.
   *
   * @param {string} name
   * @param {string|null} offerId
   * @param {Record<string, string>} props
   */
  #send(name, offerId, props) {
    // No `goal`: the optimisation objective is the engine's to choose and is no longer a
    // storefront attribute, so reporting it here would stamp a constant `aov` on every
    // event — a dimension that looks meaningful in Plausible and never varies.
    const payload = {
      ...props,
      surface: this.dataset.surface || 'cart',
      segment: this.dataset.segment,
      brand: this.dataset.brand,
    };

    const engineEvent = ENGINE_EVENT_NAMES[name];
    if (offerId && engineEvent) {
      fetch(`${this.#apiUrl}/event`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer_id: offerId, event: engineEvent, offer_sku: props.offer_sku }),
      }).catch(() => {});
    }

    window.gogroupAnalytics?.track?.(name, { params: payload });
  }
}

if (!customElements.get('offer-engine-component')) {
  customElements.define('offer-engine-component', OfferEngineComponent);
}
